/** PR / Casting agency admin queue — approve or reject pending L9/L10
 *  individuals (journalists / talent-models) who nominated this org as
 *  their verification sponsor.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ShieldCheck, ShieldOff, Mic2, UserSquare2 } from "lucide-react";

import { getBackendUrl } from "../lib/backend-url";
const API = `${getBackendUrl()}/api`;

const KIND_META = {
  journalist:   { Icon: Mic2,         label: "Journalists / Paparazzi / Influencers" },
  talent_model: { Icon: UserSquare2,  label: "Talent / Model" },
};

export default function PendingIndividuals() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState({});

  const isSponsorAdmin = user
    && ["pr_agency_admin", "casting_agency_admin"].includes(user.account_type);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/pending-individuals/mine`);
      setItems(data.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load pending list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (isSponsorAdmin) load(); }, [isSponsorAdmin]);

  const decide = async (id, action) => {
    try {
      await axios.post(`${API}/pending-individuals/${id}/decision`, {
        action, note: note[id] || "",
      });
      toast.success(action === "approve" ? "Approved" : "Rejected");
      setItems((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    }
  };

  if (!isSponsorAdmin) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16 text-center" data-testid="pending-individuals-forbidden">
        <h1 className="font-display text-3xl uppercase">Not Authorized</h1>
        <p className="text-sm text-white/60 mt-3">
          Only PR / Casting agency admins can view this queue.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-10" data-testid="pending-individuals-page">
      <div className="text-[10px] tracking-[0.3em] uppercase text-white/55">Verification Queue</div>
      <h1 className="font-display text-4xl uppercase tracking-tight mt-2">
        Pending Individuals
      </h1>
      <p className="mt-2 text-sm text-white/60 max-w-2xl">
        Individuals who nominated your agency as their verification sponsor.
        Approve to activate their account, or reject to deny.
      </p>

      <div className="mt-8 space-y-3" data-testid="pending-individuals-list">
        {loading && (
          <div className="text-sm text-white/60" data-testid="pending-individuals-loading">
            Loading…
          </div>
        )}
        {!loading && items.length === 0 && (
          <div className="cs-card rounded-2xl p-8 text-center" data-testid="pending-individuals-empty">
            <ShieldCheck size={24} className="text-emerald-400 mx-auto mb-2" />
            <div className="text-sm text-white/70">
              Nothing pending. When individuals nominate you, they&apos;ll appear here.
            </div>
          </div>
        )}
        {items.map((p) => {
          const meta = KIND_META[p.account_type] || { Icon: ShieldCheck, label: p.account_type };
          const Icon = meta.Icon;
          return (
            <div key={p.id} className="cs-card rounded-2xl p-5"
                 data-testid={`pending-individual-row-${p.id}`}>
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-white/10 text-white/80 shrink-0">
                  <Icon size={20} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="font-display text-xl">{p.name}</div>
                    <span className="text-[10px] tracking-[0.2em] uppercase text-white/50 px-2 py-0.5 rounded-full border border-white/15">
                      {meta.label}
                    </span>
                  </div>
                  <div className="text-xs text-white/55 mt-1">
                    {p.email}{p.phone ? ` · ${p.phone}` : ""}
                  </div>
                  {p.designation && (
                    <div className="text-[11px] text-white/50 mt-1">Beat: {p.designation}</div>
                  )}
                  <div className="mt-3 flex gap-2">
                    <input
                      type="text"
                      value={note[p.id] || ""}
                      onChange={(e) => setNote({ ...note, [p.id]: e.target.value })}
                      placeholder="Optional note (visible to individual)"
                      className="flex-1 bg-black/40 border border-white/15 rounded-lg h-9 px-3 text-xs"
                      data-testid={`pending-individual-note-${p.id}`}
                    />
                    <Button
                      onClick={() => decide(p.id, "approve")}
                      className="h-9 px-4 text-xs uppercase tracking-[0.16em]"
                      data-testid={`pending-individual-approve-${p.id}`}>
                      <ShieldCheck size={14} className="mr-1.5" /> Approve
                    </Button>
                    <Button
                      onClick={() => decide(p.id, "reject")}
                      variant="outline"
                      className="h-9 px-4 text-xs uppercase tracking-[0.16em] border-white/20"
                      data-testid={`pending-individual-reject-${p.id}`}>
                      <ShieldOff size={14} className="mr-1.5" /> Reject
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
